import type { CSSProperties } from 'react';

/** Lines a user-typed name may take before it is clamped with an ellipsis. */
export const LONG_NAME_MAX_LINES = 2;

/**
 * Bounds a user-typed name (a channel name can be 255 characters, release
 * 29817) so it cannot widen its container. `overflow-wrap: anywhere` is the
 * part that matters for layout: unlike `break-word` it also lowers the
 * element's min-content width, so a table column or a flex item can shrink
 * below the length of an unbroken name. The clamp then caps the height.
 *
 * The text itself is left whole, so it stays the accessible name; callers add
 * `title` with the same text so the clamped part is reachable by pointer.
 *
 * Kebab-case, the form `styleMap` takes in a Lit template (a cell inside
 * `ps-data-table`'s shadow root, which no stylesheet of the app reaches);
 * `LONG_NAME_REACT_STYLE` is the same declarations for a React `style` prop.
 */
export const LONG_NAME_STYLE: Readonly<Record<string, string>> = {
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': String(LONG_NAME_MAX_LINES),
  overflow: 'hidden',
  'overflow-wrap': 'anywhere',
};

/** `-webkit-line-clamp` → `WebkitLineClamp`, `overflow-wrap` → `overflowWrap`. */
const toReactProperty = (property: string) =>
  property
    .replace(/^-webkit-/, 'Webkit-')
    .replace(/-([a-z])/gi, (_m, c: string) => c.toUpperCase());

export const LONG_NAME_REACT_STYLE = Object.fromEntries(
  Object.entries(LONG_NAME_STYLE).map(([k, v]) => [toReactProperty(k), v])
) as CSSProperties;
